/**
 * svmModel - the SVM checklist feature's pure layer. NO JSX, no fetching.
 *
 * Owns: the shared inline-style constants (buttons, cards, bottom-sheet and
 * popover chrome), the two shared hooks (useIsNarrow phone detection,
 * useDismiss outside-click/Escape), the formatters (initials, short date, full
 * stamp, "Xs ago"), zone-scope normalisation/compare, the EFFECTIVE-zone
 * derivation, the optimistic-merge rule for polled checks, and every bit of
 * progress math (zone totals, zone stats, overall, per-phase/sub stats).
 */
import { useEffect, useState } from 'react';

export const GREEN = '#1B5E20';
// Fallback only - the ACTIVE discipline comes from the card the user picked
// and is threaded through the zone step (Process and Controls are different
// books with different templates).
export const DEFAULT_DISCIPLINE = { id: 'svm-sealer-process', label: 'Process' };

export const CARD = {
  background: '#fff',
  border: '1px solid #ECECEC',
  borderRadius: 12,
  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  padding: '18px 20px',
  marginBottom: 16,
};
export const LABEL = {
  fontSize: 9.5,
  fontWeight: 800,
  color: '#999',
  letterSpacing: 1,
  textTransform: 'uppercase',
  marginBottom: 4,
  display: 'block',
};
export const INPUT = {
  width: '100%',
  boxSizing: 'border-box',
  fontFamily: "'Montserrat', sans-serif",
  fontSize: 12.5,
  color: '#0D0D0D',
  padding: '8px 10px',
  border: '1px solid #DDD',
  borderRadius: 8,
  background: '#fff',
  outline: 'none',
};
export const BTN = {
  fontFamily: "'Montserrat', sans-serif",
  fontSize: 12,
  fontWeight: 700,
  border: 'none',
  borderRadius: 8,
  padding: '8px 14px',
  cursor: 'pointer',
};
export const BTN_PRIMARY = { ...BTN, background: GREEN, color: '#fff' };
export const BTN_GHOST = { ...BTN, background: '#F2F2F2', color: '#333' };
export const LINKISH = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  fontFamily: "'Montserrat', sans-serif",
  fontSize: 'inherit',
  fontWeight: 'inherit',
  color: 'inherit',
};

// ── phone detection (portrait ~375-430px): shared matchMedia hook ──
// Components branch on this for the narrow layout; desktop styles are the
// exact objects from before, so ≥641px rendering is unchanged.
const NARROW_MQ = '(max-width: 640px)';
export function useIsNarrow() {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(NARROW_MQ).matches : false
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(NARROW_MQ);
    const onChange = (e) => setNarrow(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange); // older Safari
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return narrow;
}

// Bottom-sheet chrome shared by CellPopover and ZoneScopePanel on narrow.
export const SHEET_BOX = {
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1000,
  background: '#fff',
  border: '1px solid #DDD',
  borderBottom: 'none',
  borderRadius: '14px 14px 0 0',
  boxShadow: '0 -6px 24px rgba(0,0,0,0.2)',
  padding: '10px 16px 18px',
  fontFamily: "'Montserrat', sans-serif",
  maxHeight: '70vh',
  overflowY: 'auto',
  boxSizing: 'border-box',
};
export const SHEET_BACKDROP = { position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, background: 'rgba(0,0,0,0.35)', zIndex: 999 };
export const SHEET_HANDLE = { width: 36, height: 4, borderRadius: 2, background: '#DDD', margin: '0 auto 10px' };

// Desktop floating-panel chrome shared by SharePanel / CellPopover /
// ZoneScopePanel - each spreads its own clamped { left, top, width } over it.
export const POPOVER_BOX = {
  position: 'fixed',
  zIndex: 1000,
  background: '#fff',
  border: '1px solid #DDD',
  borderRadius: 12,
  boxShadow: '0 6px 24px rgba(0,0,0,0.16)',
  padding: 14,
  fontFamily: "'Montserrat', sans-serif",
  maxHeight: '62vh',
  overflowY: 'auto',
  boxSizing: 'border-box',
};

// Close on outside mousedown / Escape - shared by every floating panel
// (SharePanel, CellPopover, ZoneScopePanel).
export function useDismiss(ref, onClose) {
  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [ref, onClose]);
}

// ── small helpers ──
export const keyOf = (zone, itemNo, col) => `${zone}|${itemNo}|${col}`;

export const initialsOf = (name) => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};
export const shortDate = (ts) => {
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? '' : `${d.getMonth() + 1}/${d.getDate()}`;
};
export const fullStamp = (ts) => {
  const d = new Date(ts);
  return Number.isNaN(d.getTime())
    ? '-'
    : d.toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};
// "Synced Xs ago" wording for the header sync indicator.
export const relTime = (lastSync) => {
  const s = Math.max(0, Math.round((Date.now() - lastSync) / 1000));
  return s < 10 ? 'just now' : s < 90 ? `${s}s ago` : `${Math.round(s / 60)}m ago`;
};
// "Frames in Sim (Ch. 12)" → "Frames in Sim" (the chapter chip carries the tag)
export const stripChapter = (name) => String(name || '').replace(/\s*\(Ch\.\s*\d+\)\s*$/i, '');

// The sign-off columns one item owns: one whole-cell column, or one per robot.
export const colsOf = (item, robots) => (item.scope === 'cell' ? ['CELL'] : robots);

export const zoneTotalCells = (z) => {
  let total = 0;
  for (const ph of z.phases || []) {
    for (const sub of ph.subsections || []) {
      for (const it of sub.items || []) total += it.scope === 'cell' ? 1 : (z.robots || []).length;
    }
  }
  return total;
};

export const zoneItemCount = (z) => {
  let n = 0;
  for (const ph of z.phases || []) for (const sub of ph.subsections || []) n += (sub.items || []).length;
  return n;
};

// Zone scope = array of { key, robots } (robots = how many robots this project
// actually runs in that zone), or null meaning ALL zones at full template
// robots (no scope set). Legacy responses may still send plain key strings —
// those normalize to robots: null, which means "full template count" and is
// resolved against the template when the effective zones are derived.
export const normalizeScope = (zones) => {
  if (!Array.isArray(zones)) return null;
  return zones.map((z) =>
    typeof z === 'string' ? { key: z, robots: null } : { key: z.key, robots: Number.isInteger(z.robots) && z.robots > 0 ? z.robots : null }
  );
};

// Order-insensitive compare (key + robots) so poll responses never churn state identity.
export const sameZoneScope = (a, b) =>
  Array.isArray(a) &&
  Array.isArray(b) &&
  a.length === b.length &&
  a.every((za) => b.some((zb) => zb.key === za.key && zb.robots === za.robots));

// null scope = every zone is in scope.
export const scopeHasKey = (scope, key) => !scope || scope.some((s) => s.key === key);

// Merge a zones field from any server response. Identity-stable when the
// scope (key set + robot counts) is unchanged so poll ticks don't re-render
// or refire fetch effects.
export const nextZoneScope = (prev, zones) => {
  const next = normalizeScope(zones);
  if (next === null) return prev === null ? prev : null;
  return sameZoneScope(prev, next) ? prev : next;
};

// EFFECTIVE zones: template zones in this project's scope (template order),
// each with its robots array sliced to the scoped count. Everything in the
// zone view (grid, chips, totals, filter, prefetch) runs off these.
// null scope = all template zones at full robots.
export function deriveScopedZones(template, zoneScope) {
  if (!template) return [];
  if (!zoneScope) return template.zones;
  const byKey = new Map(zoneScope.map((s) => [s.key, s]));
  return template.zones
    .filter((z) => byKey.has(z.key))
    .map((z) => {
      const full = (z.robots || []).length;
      const want = byKey.get(z.key).robots;
      const n = want == null ? full : Math.max(1, Math.min(want, full));
      return n === full ? z : { ...z, robots: (z.robots || []).slice(0, n) };
    });
}

// Merge server state for one zone: server wins EXCEPT in-flight optimistic
// keys. Returns `prev` untouched when the poll brought nothing new, so the big
// checklist tree skips the re-render.
export function mergeServerChecks(prev, zone, serverChecks, pending) {
  const next = new Map(prev);
  const prefix = `${zone}|`;
  for (const k of Array.from(next.keys())) {
    if (k.startsWith(prefix) && !pending.has(k)) next.delete(k);
  }
  for (const c of serverChecks) {
    const k = keyOf(zone, c.itemNo, c.col);
    if (!pending.has(k)) next.set(k, c);
  }
  // Skip the re-render when the poll brought nothing new.
  if (next.size === prev.size) {
    let same = true;
    for (const [k, v] of next) {
      const p = prev.get(k);
      if (!p || p.status !== v.status || p.note !== v.note || p.checkedAt !== v.checkedAt || p.checkedBy !== v.checkedBy) {
        same = false;
        break;
      }
    }
    if (same) return prev;
  }
  return next;
}

// ── derived stats (effective zones: scoped keys, sliced robots) ──
export const pctOf = (signed, total) => (total > 0 ? Math.round((100 * signed) / total) : 0);

export function computeZoneStats(scopedZones, checks) {
  const out = {};
  const colsByZone = {};
  for (const z of scopedZones) {
    out[z.key] = { total: zoneTotalCells(z), signed: 0 };
    colsByZone[z.key] = new Set(['CELL', ...(z.robots || [])]);
  }
  for (const k of checks.keys()) {
    const zone = k.slice(0, k.indexOf('|'));
    const col = k.slice(k.lastIndexOf('|') + 1);
    // Ignore checks on robots beyond this project's count (stale data).
    if (out[zone] && colsByZone[zone].has(col)) out[zone].signed += 1;
  }
  return out;
}

export function computeOverall(zoneStats, scopedZones) {
  let total = 0;
  let signed = 0;
  for (const z of scopedZones) {
    const st = zoneStats[z.key];
    if (st) {
      total += st.total;
      signed += st.signed;
    }
  }
  return { total, signed };
}

// Mod-Build vs On-Site progress for the ACTIVE zone. Two book shapes carry
// the same story two ways (mirrors server/svm/templates.js modSiteLookup):
//   - COLUMN-based (Controls / Install): each item signs a 'Mod' cell and a
//     'Site' cell - the Mod column IS the modular build. Whole-row CELL
//     sign-offs count toward On-Site (they're field buy-off lines).
//   - PHASE-based (Lucas's Process book, robots R1..R6): its phases are
//     OFFLINE WORK / MOD BUILD / COMMISSIONING - the first two happen before
//     the truck ships, commissioning is on-site.
// A book with neither shape returns null - single bar as before.
export const MOD_PHASE_RE = /offline|mod\s*build/i;
export function computeModSiteStats(zoneDef, checks) {
  if (!zoneDef) return null;
  const colBased = (zoneDef.robots || []).includes('Mod');
  const phaseBased = !colBased && (zoneDef.phases || []).some((p) => MOD_PHASE_RE.test(p.name || ''));
  if (!colBased && !phaseBased) return null;
  const mod = { t: 0, s: 0 };
  const site = { t: 0, s: 0 };
  for (const ph of zoneDef.phases || []) {
    const phaseBucket = phaseBased && MOD_PHASE_RE.test(ph.name || '') ? mod : site;
    for (const sub of ph.subsections || []) {
      for (const it of sub.items || []) {
        for (const c of colsOf(it, zoneDef.robots)) {
          const g = colBased ? (c === 'Mod' ? mod : site) : phaseBucket;
          g.t += 1;
          if (checks.has(keyOf(zoneDef.key, it.no, c))) g.s += 1;
        }
      }
    }
  }
  return { mod, site };
}

// Per-phase and per-subsection signed/total for the ACTIVE zone.
export function computeActiveStats(zoneDef, checks) {
  if (!zoneDef) return { phases: [], subs: {} };
  const phases = [];
  const subs = {};
  for (const ph of zoneDef.phases || []) {
    let pT = 0;
    let pS = 0;
    for (const sub of ph.subsections || []) {
      let t = 0;
      let s = 0;
      for (const it of sub.items || []) {
        const cols = colsOf(it, zoneDef.robots);
        t += cols.length;
        for (const c of cols) if (checks.has(keyOf(zoneDef.key, it.no, c))) s += 1;
      }
      subs[`${ph.name}|${sub.name}`] = { t, s };
      pT += t;
      pS += s;
    }
    phases.push({ name: ph.name, t: pT, s: pS });
  }
  return { phases, subs };
}

// If a scope edit shrank the active zone's robots below the current filter
// (or a zone switch changed the robot set), fall back to ALL.
export const safeRobotFilter = (robotFilter, zoneDef) =>
  robotFilter === 'ALL' || (zoneDef && (zoneDef.robots || []).includes(robotFilter)) ? robotFilter : 'ALL';
